import { useEffect, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Printer, Download, Loader2 } from 'lucide-react';

const W = 1240;
const H = 1754;

const loadImage = (src) => new Promise((resolve) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => resolve(img);
  img.onerror = () => resolve(null);
  img.src = src;
});

const wrap = (ctx, text, maxWidth) => {
  const words = text.split(' ');
  const lines = [];
  let line = '';
  words.forEach((w) => {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else line = test;
  });
  if (line) lines.push(line);
  return lines;
};

/** Locandina A4 pronta da stampare: logo, invito, QR e indirizzo della pagina. */
export const BookingPosterDialog = ({ open, onOpenChange, url, salonName, logoUrl, accent, phone, address }) => {
  const qrRef = useRef(null);
  const previewRef = useRef(null);
  const [headline, setHeadline] = useState('Prenota il tuo appuntamento');
  const [subline, setSubline] = useState('Inquadra il QR code con la fotocamera');
  const [busy, setBusy] = useState(false);

  const color = accent || '#7c3aed';

  const build = async () => {
    const qrCanvas = qrRef.current?.querySelector('canvas');
    if (!qrCanvas) return null;
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, W, 26);
    ctx.fillRect(0, H - 26, W, 26);

    let y = 150;
    const logo = logoUrl ? await loadImage(logoUrl) : null;
    if (logo) {
      const maxH = 150;
      const ratio = Math.min(420 / logo.width, maxH / logo.height);
      const w = logo.width * ratio;
      const h = logo.height * ratio;
      ctx.drawImage(logo, (W - w) / 2, y, w, h);
      y += h + 60;
    }

    ctx.textAlign = 'center';
    if (salonName) {
      ctx.fillStyle = '#111827';
      ctx.font = '600 56px Outfit, Helvetica, Arial, sans-serif';
      ctx.fillText(salonName, W / 2, y + 40);
      y += 100;
    }

    ctx.fillStyle = color;
    ctx.font = '700 84px Outfit, Helvetica, Arial, sans-serif';
    const headLines = wrap(ctx, headline, W - 180);
    headLines.forEach((line, i) => ctx.fillText(line, W / 2, y + 70 + i * 98));
    y += 70 + headLines.length * 98;

    ctx.fillStyle = '#4b5563';
    ctx.font = '400 40px Helvetica, Arial, sans-serif';
    const subLines = wrap(ctx, subline, W - 200);
    subLines.forEach((line, i) => ctx.fillText(line, W / 2, y + 40 + i * 54));
    y += 40 + subLines.length * 54 + 50;

    const qrSize = 620;
    const qx = (W - qrSize) / 2;
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 4;
    ctx.strokeRect(qx - 24, y - 24, qrSize + 48, qrSize + 48);
    ctx.drawImage(qrCanvas, qx, y, qrSize, qrSize);
    y += qrSize + 90;

    ctx.fillStyle = '#111827';
    ctx.font = '600 36px Helvetica, Arial, sans-serif';
    ctx.fillText(url.replace(/^https?:\/\//, ''), W / 2, y);
    y += 60;

    const footer = [phone, address].filter(Boolean).join('  ·  ');
    if (footer) {
      ctx.fillStyle = '#6b7280';
      ctx.font = '400 32px Helvetica, Arial, sans-serif';
      ctx.fillText(footer, W / 2, y + 10);
    }
    return canvas;
  };

  const renderPreview = async () => {
    const canvas = await build();
    if (!canvas || !previewRef.current) return;
    previewRef.current.innerHTML = '';
    canvas.style.width = '100%';
    canvas.style.display = 'block';
    canvas.style.borderRadius = '8px';
    previewRef.current.appendChild(canvas);
  };

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(renderPreview, 120);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, headline, subline, logoUrl, salonName, url]);

  const download = async () => {
    setBusy(true);
    const canvas = await build();
    setBusy(false);
    if (!canvas) return;
    const a = document.createElement('a');
    a.download = 'locandina-prenotazioni.png';
    a.href = canvas.toDataURL('image/png');
    a.click();
    toast.success('Locandina scaricata');
  };

  const print = async () => {
    setBusy(true);
    const canvas = await build();
    setBusy(false);
    if (!canvas) return;
    const win = window.open('', '_blank');
    if (!win) {
      toast.error('Consenti le finestre popup per stampare');
      return;
    }
    win.document.write(
      `<html><head><title>Locandina prenotazioni</title><style>
        @page { size: A4; margin: 0 }
        html,body { margin:0; padding:0 }
        img { width:100%; display:block }
      </style></head><body><img src="${canvas.toDataURL('image/png')}" /></body></html>`
    );
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 400);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="poster-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit">Locandina da stampare</DialogTitle>
        </DialogHeader>

        <div ref={qrRef} className="hidden">
          <QRCodeCanvas value={url} size={620} level="M" includeMargin={false} />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="poster-headline" className="text-xs">Invito</Label>
              <Input id="poster-headline" value={headline} maxLength={46}
                onChange={(e) => setHeadline(e.target.value)} data-testid="poster-headline-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="poster-subline" className="text-xs">Riga sotto</Label>
              <Input id="poster-subline" value={subline} maxLength={60}
                onChange={(e) => setSubline(e.target.value)} data-testid="poster-subline-input" />
            </div>
            <p className="text-xs text-muted-foreground">
              Formato A4. Logo, nome e colore arrivano dalle impostazioni azienda.
            </p>
            <div className="flex flex-col gap-2">
              <Button onClick={print} disabled={busy} className="min-h-[44px]" data-testid="print-poster">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
                Stampa
              </Button>
              <Button variant="outline" onClick={download} disabled={busy} className="min-h-[44px]" data-testid="download-poster">
                <Download className="w-4 h-4 mr-2" />Scarica PNG
              </Button>
            </div>
          </div>
          <div ref={previewRef} className="border rounded-lg overflow-hidden bg-white min-h-[240px]"
            data-testid="poster-preview" />
        </div>
      </DialogContent>
    </Dialog>
  );
};
